import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { StreakHistoryParams, StreakStatus } from '@/types/streak.types';
import { streakApi } from '../api/streak.api';

export const STREAK_QUERY_KEYS = {
  all: ['streak'] as const,
  status: () => [...STREAK_QUERY_KEYS.all, 'status'] as const,
  history: (params: StreakHistoryParams) => [...STREAK_QUERY_KEYS.all, 'history', params] as const,
  leaderboard: () => [...STREAK_QUERY_KEYS.all, 'leaderboard'] as const,
};

/**
 * Fetch the current user's streak status (today's progress, tiers, points,
 * freezes). Polled so the header pill / portfolio widget stay fresh even
 * without a trade — `useClobOrder`'s post-trade invalidation gives faster
 * updates right after the user actually trades.
 */
export function useStreakStatus(isAuthenticated: boolean) {
  return useQuery({
    queryKey: STREAK_QUERY_KEYS.status(),
    queryFn: streakApi.getStatus,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    enabled: isAuthenticated,
  });
}

/**
 * Fetch paginated streak points history.
 */
export function useStreakHistory(params: StreakHistoryParams = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: STREAK_QUERY_KEYS.history(params),
    queryFn: () => streakApi.getHistory(params),
    staleTime: 60 * 1000,
    enabled,
  });
}

/**
 * Fetch the top-9 streak leaderboard plus the current user's own rank.
 */
export function useStreakLeaderboard(isAuthenticated: boolean) {
  return useQuery({
    queryKey: STREAK_QUERY_KEYS.leaderboard(),
    queryFn: streakApi.getLeaderboard,
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
  });
}

/**
 * Mutation to spend points on a streak freeze.
 */
export function usePurchaseFreeze() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => streakApi.purchaseFreeze(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STREAK_QUERY_KEYS.all });
      toast.success('Streak freeze purchased');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to purchase streak freeze');
    },
  });
}

/**
 * Mutation to permanently dismiss the top-of-page streak banner. Optimistically
 * flips the cached status so the banner disappears immediately on click rather
 * than waiting on a round trip / the next 30s poll.
 */
export function useDismissStreakBanner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => streakApi.dismissBanner(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: STREAK_QUERY_KEYS.status() });
      const previous = queryClient.getQueryData<StreakStatus>(STREAK_QUERY_KEYS.status());
      if (previous) {
        queryClient.setQueryData<StreakStatus>(STREAK_QUERY_KEYS.status(), {
          ...previous,
          bannerDismissed: true,
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(STREAK_QUERY_KEYS.status(), context.previous);
      }
    },
  });
}
